import { CartAddEvent } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

const STORAGE_KEY = 'bce-site-view-mode';
const MOBILE_GRID_KEY = 'product-grid-view-mobile';
const DESKTOP_GRID_KEY = 'product-grid-view-desktop';
const BUY_NOW_PENDING_KEY = 'bce-isolated-buy-now-cart-v1';
const OPTIMISTIC_CART_WINDOW = 5000;
let buyNowRestorePromise = null;
const root = document.documentElement;
const viewport = document.querySelector('meta[name="viewport"]');
const smallTouchDevice =
  window.matchMedia('(pointer: coarse)').matches &&
  Math.min(window.screen.width, window.screen.height) <= 900;
const desktopMedia = window.matchMedia('(min-width: 750px)');
root.dataset.bcePhysicalMobile = smallTouchDevice ? 'true' : 'false';

function fetchWithTimeout(url, options = {}, timeout = 7000) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeout);
  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => window.clearTimeout(timeoutId));
}

function promiseWithTimeout(promise, timeout = 7000) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      window.setTimeout(() => reject(new Error('The request took too long')), timeout);
    }),
  ]);
}

function currentMode() {
  if (!smallTouchDevice) return 'desktop-native';
  return localStorage.getItem(STORAGE_KEY) === 'desktop' ? 'desktop' : 'mobile';
}

function isPhysicalMobileView() {
  return root.dataset.bcePhysicalMobile === 'true' && root.dataset.bceViewMode === 'mobile';
}

let mobileCardAddPromise = null;
let mobileCartDrawerSkeletonPromise = null;
let mobileProductAddPending = null;
let desktopCartSkeletonPromise = null;
let desktopProductSkeletonPending = null;

function trackMobileCartAdd(request) {
  if (!isPhysicalMobileView() || !request || typeof request.then !== 'function') return request;

  const pendingRequest = mobileCardAddPromise
    ? Promise.allSettled([mobileCardAddPromise, request])
    : request;

  mobileCardAddPromise = pendingRequest;
  setMobileCartTriggerPending(true);

  const clearPendingRequest = () => {
    if (mobileCardAddPromise !== pendingRequest) return;
    mobileCardAddPromise = null;
    setMobileCartTriggerPending(false);
  };

  pendingRequest.then(clearPendingRequest, clearPendingRequest);
  return pendingRequest;
}

function ensureDesktopCartSkeletonStyles() {
  if (document.getElementById('bce-desktop-cart-skeleton-styles')) return;

  const style = document.createElement('style');
  style.id = 'bce-desktop-cart-skeleton-styles';
  style.textContent = `
    .bce-cart-sync-skeleton {
      position: absolute;
      inset: 72px 0 0;
      z-index: 4;
      display: flex;
      flex-direction: column;
      gap: 28px;
      padding: 28px 32px 34px;
      overflow: hidden;
      background: var(--color-background);
      pointer-events: none;
    }

    .bce-cart-sync-skeleton__row {
      display: grid;
      grid-template-columns: 96px minmax(0, 1fr);
      gap: 18px;
      align-items: start;
      padding-bottom: 28px;
      border-bottom: var(--style-border-width, 1px) solid
        rgb(var(--color-foreground-rgb) / 0.1);
    }

    .bce-cart-sync-skeleton__details,
    .bce-cart-sync-skeleton__summary {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .bce-cart-sync-skeleton__summary {
      margin-top: auto;
      padding-top: 24px;
      border-top: var(--style-border-width, 1px) solid
        rgb(var(--color-foreground-rgb) / 0.1);
    }

    .bce-cart-sync-skeleton__block {
      display: block;
      min-height: 14px;
      border-radius: 8px;
      background-image: linear-gradient(
        -85deg,
        rgb(var(--color-foreground-rgb) / 0.08) 10%,
        rgb(var(--color-foreground-rgb) / 0.18) 50%,
        rgb(var(--color-foreground-rgb) / 0.08) 90%
      );
      background-size: 200% 100%;
      background-position: 100% 0;
      animation: text-shimmer 1.25s infinite linear;
    }

    .bce-cart-sync-skeleton__image {
      width: 96px;
      aspect-ratio: 1;
      border-radius: 12px;
    }

    .bce-cart-sync-skeleton__title {
      width: 88%;
      min-height: 18px;
    }

    .bce-cart-sync-skeleton__line {
      width: 64%;
    }

    .bce-cart-sync-skeleton__line--short {
      width: 42%;
    }

    .bce-cart-sync-skeleton__summary-line {
      width: 100%;
      min-height: 16px;
    }

    .bce-cart-sync-skeleton__summary-line--short {
      width: 58%;
      align-self: flex-end;
    }

    html[data-bce-physical-mobile='true'][data-bce-view-mode='mobile']
      .bce-cart-sync-skeleton {
      display: none;
    }

    html[data-bce-physical-mobile='true'][data-bce-view-mode='mobile']
      .bce-cart-sync-skeleton.bce-cart-sync-skeleton--mobile {
      inset: 64px 0 0;
      display: flex;
      gap: 24px;
      padding: 24px 22px 28px;
    }

    html[data-bce-physical-mobile='true'][data-bce-view-mode='mobile']
      .bce-cart-sync-skeleton--mobile .bce-cart-sync-skeleton__row {
      grid-template-columns: 84px minmax(0, 1fr);
      gap: 16px;
      padding-bottom: 24px;
    }

    html[data-bce-physical-mobile='true'][data-bce-view-mode='mobile']
      .bce-cart-sync-skeleton--mobile .bce-cart-sync-skeleton__image {
      width: 84px;
    }

    @media (prefers-reduced-motion: reduce) {
      .bce-cart-sync-skeleton__block {
        animation: none;
      }
    }
  `;
  document.head.appendChild(style);
}

function setDesktopCartSkeleton(active) {
  document.querySelectorAll('[data-bce-cart-sync-skeleton]').forEach((node) => node.remove());
  document.querySelectorAll('cart-drawer-component [data-testid="cart-drawer-dialog"][aria-busy="true"]').forEach((dialog) => {
    dialog.removeAttribute('aria-busy');
  });

  if (!active || isPhysicalMobileView()) return;

  const dialog = document.querySelector(
    'cart-drawer-component [data-testid="cart-drawer-dialog"]'
  );
  if (!(dialog instanceof HTMLDialogElement)) return;

  ensureDesktopCartSkeletonStyles();

  const skeleton = document.createElement('div');
  skeleton.className = 'bce-cart-sync-skeleton';
  skeleton.dataset.bceCartSyncSkeleton = '';
  skeleton.setAttribute('aria-hidden', 'true');
  skeleton.innerHTML = `
    <div class="bce-cart-sync-skeleton__row">
      <span class="bce-cart-sync-skeleton__block bce-cart-sync-skeleton__image"></span>
      <span class="bce-cart-sync-skeleton__details">
        <span class="bce-cart-sync-skeleton__block bce-cart-sync-skeleton__title"></span>
        <span class="bce-cart-sync-skeleton__block bce-cart-sync-skeleton__line"></span>
        <span class="bce-cart-sync-skeleton__block bce-cart-sync-skeleton__line bce-cart-sync-skeleton__line--short"></span>
      </span>
    </div>
    <div class="bce-cart-sync-skeleton__summary">
      <span class="bce-cart-sync-skeleton__block bce-cart-sync-skeleton__summary-line"></span>
      <span class="bce-cart-sync-skeleton__block bce-cart-sync-skeleton__summary-line bce-cart-sync-skeleton__summary-line--short"></span>
    </div>
  `;

  dialog.setAttribute('aria-busy', 'true');
  dialog.appendChild(skeleton);
}

function setMobileCartSkeleton(active) {
  document
    .querySelectorAll('[data-bce-mobile-cart-sync-skeleton]')
    .forEach((node) => node.remove());

  const dialog = document.querySelector(
    'cart-drawer-component [data-testid="cart-drawer-dialog"]'
  );

  if (dialog instanceof HTMLDialogElement) {
    const wasMobileSyncBusy = dialog.hasAttribute('data-bce-mobile-cart-sync-busy');
    dialog.removeAttribute('data-bce-mobile-cart-sync-busy');
    if (wasMobileSyncBusy) dialog.removeAttribute('aria-busy');
  }

  if (!active || !isPhysicalMobileView() || !(dialog instanceof HTMLDialogElement)) return;

  ensureDesktopCartSkeletonStyles();

  const skeleton = document.createElement('div');
  skeleton.className = 'bce-cart-sync-skeleton bce-cart-sync-skeleton--mobile';
  skeleton.dataset.bceMobileCartSyncSkeleton = '';
  skeleton.setAttribute('aria-hidden', 'true');
  skeleton.innerHTML = `
    <div class="bce-cart-sync-skeleton__row">
      <span class="bce-cart-sync-skeleton__block bce-cart-sync-skeleton__image"></span>
      <span class="bce-cart-sync-skeleton__details">
        <span class="bce-cart-sync-skeleton__block bce-cart-sync-skeleton__title"></span>
        <span class="bce-cart-sync-skeleton__block bce-cart-sync-skeleton__line"></span>
        <span class="bce-cart-sync-skeleton__block bce-cart-sync-skeleton__line bce-cart-sync-skeleton__line--short"></span>
      </span>
    </div>
    <div class="bce-cart-sync-skeleton__summary">
      <span class="bce-cart-sync-skeleton__block bce-cart-sync-skeleton__summary-line"></span>
      <span class="bce-cart-sync-skeleton__block bce-cart-sync-skeleton__summary-line bce-cart-sync-skeleton__summary-line--short"></span>
    </div>
  `;

  dialog.dataset.bceMobileCartSyncBusy = 'true';
  dialog.setAttribute('aria-busy', 'true');
  dialog.appendChild(skeleton);
}

function trackMobileCartDrawerSkeleton(request) {
  if (!isPhysicalMobileView() || !request || typeof request.then !== 'function') return request;

  const pendingRequest = Promise.resolve(request);
  mobileCartDrawerSkeletonPromise = pendingRequest;
  setMobileCartSkeleton(true);

  const clearSkeleton = () => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (mobileCartDrawerSkeletonPromise !== pendingRequest) return;
        mobileCartDrawerSkeletonPromise = null;
        setMobileCartSkeleton(false);
      });
    });
  };

  pendingRequest.then(clearSkeleton, clearSkeleton);
  return request;
}

function desktopCartDrawerHasItems() {
  const drawer = document.querySelector('cart-drawer-component');
  return Boolean(
    drawer?.querySelector(
      '.cart-form .cart-items__table-row[data-key], .cart-form [ref="cartItemRows[]"]'
    )
  );
}

function waitForDesktopCartDrawerItems(timeout = 2500) {
  if (desktopCartDrawerHasItems()) return Promise.resolve();

  return new Promise((resolve) => {
    let settled = false;
    let timeoutId = 0;
    const rootNode = document.querySelector('header-actions') || document.body;
    const observer = new MutationObserver(() => {
      if (!desktopCartDrawerHasItems()) return;
      finish();
    });

    const finish = () => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      window.clearTimeout(timeoutId);
      window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
    };

    if (rootNode) {
      observer.observe(rootNode, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    timeoutId = window.setTimeout(finish, timeout);
  });
}

function trackDesktopCartSkeleton(request, form) {
  if (
    isPhysicalMobileView() ||
    !request ||
    typeof request.then !== 'function' ||
    desktopCartDrawerHasItems()
  ) {
    return request;
  }

  const pendingRequest = desktopCartSkeletonPromise
    ? Promise.allSettled([desktopCartSkeletonPromise, request])
    : Promise.resolve(request).then(async () => {
        const button = form?.querySelector('button[type="submit"]');
        if (button instanceof HTMLButtonElement && button.dataset.bceError === 'true') return;
        await waitForDesktopCartDrawerItems();
      });

  desktopCartSkeletonPromise = pendingRequest;
  setDesktopCartSkeleton(true);

  const clearSkeleton = () => {
    if (desktopCartSkeletonPromise !== pendingRequest) return;
    desktopCartSkeletonPromise = null;
    setDesktopCartSkeleton(false);
  };

  pendingRequest.then(clearSkeleton, clearSkeleton);
  return request;
}

function beginDesktopProductSkeleton(form) {
  if (
    isPhysicalMobileView() ||
    currentRenderedCartCount() !== 0 ||
    desktopCartDrawerHasItems()
  ) {
    return;
  }

  desktopProductSkeletonPending?.cancel();

  let resolveRequest;
  const request = new Promise((resolve) => {
    resolveRequest = resolve;
  });

  const pending = {
    settled: false,
    timeoutId: 0,
    settle(didError = false) {
      if (this.settled) return;
      this.settled = true;
      window.clearTimeout(this.timeoutId);
      if (desktopProductSkeletonPending === this) desktopProductSkeletonPending = null;

      if (didError && desktopCartSkeletonPromise) {
        desktopCartSkeletonPromise = null;
        setDesktopCartSkeleton(false);
      }

      resolveRequest();
    },
    cancel() {
      this.settle(true);
    },
  };

  pending.timeoutId = window.setTimeout(() => pending.cancel(), 10000);
  desktopProductSkeletonPending = pending;
  trackDesktopCartSkeleton(request, form);
}

function settleDesktopProductSkeleton(didError = false) {
  desktopProductSkeletonPending?.settle(didError);
}

function beginMobileProductAdd() {
  if (!isPhysicalMobileView()) return;

  mobileProductAddPending?.settle();

  let resolveRequest;
  const request = new Promise((resolve) => {
    resolveRequest = resolve;
  });

  const pending = {
    settled: false,
    timeoutId: 0,
    settle() {
      if (this.settled) return;
      this.settled = true;
      window.clearTimeout(this.timeoutId);
      if (mobileProductAddPending === this) mobileProductAddPending = null;
      resolveRequest();
    },
  };

  pending.timeoutId = window.setTimeout(() => {
    pending.settle();
    window.setTimeout(syncCartIndicators, 0);
  }, 10000);

  mobileProductAddPending = pending;
  trackMobileCartAdd(request);
}

function settleMobileProductAdd() {
  mobileProductAddPending?.settle();
}

function setMobileCartTriggerPending(pending) {
  document.querySelectorAll('[data-testid="cart-drawer-trigger"]').forEach((trigger) => {
    if (!(trigger instanceof HTMLButtonElement)) return;

    trigger.toggleAttribute('data-bce-cart-add-pending', pending);
    trigger.removeAttribute('data-bce-waiting-for-cart-add');
  });
}

function updateViewButtons(mode) {
  document.querySelectorAll('[data-bce-view]').forEach((button) => {
    const pressed = String(button.dataset.bceView === mode);
    if (button.getAttribute('aria-pressed') !== pressed) {
      button.setAttribute('aria-pressed', pressed);
    }
  });
}

function applyMode(mode) {
  root.dataset.bceViewMode = mode;

  if (viewport && smallTouchDevice) {
    viewport.setAttribute(
      'content',
      mode === 'desktop'
        ? 'width=1280, initial-scale=0.3, maximum-scale=5, user-scalable=yes'
        : 'width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes'
    );
  }

  syncViewportFixedViewSwitch(mode);
  updateViewButtons(mode);
}

function currentMobileColumns() {
  return sessionStorage.getItem(MOBILE_GRID_KEY) === 'mobile-single' ? '1' : '2';
}

function updateMobileColumnButtons(columns) {
  document.querySelectorAll('[data-bce-product-columns]').forEach((button) => {
    button.setAttribute('aria-pressed', String(button.dataset.bceProductColumns === columns));
  });

  const nativeValue = columns === '1' ? 'mobile-single' : 'default';
  document.querySelectorAll('input[name="grid-mobile"]').forEach((input) => {
    if (input instanceof HTMLInputElement) input.checked = input.value === nativeValue;
  });
}

function restoreProductGrid() {
  const desktopLayout = desktopMedia.matches;
  const mobileColumns = currentMobileColumns();
  const mobileView = mobileColumns === '1' ? 'mobile-single' : 'default';
  const desktopView = sessionStorage.getItem(DESKTOP_GRID_KEY) || 'default';

  document.querySelectorAll('ul.product-grid').forEach((grid) => {
    if (desktopLayout) {
      grid.setAttribute('product-grid-view', desktopView);
      grid.style.removeProperty('grid-template-columns');
      return;
    }

    grid.setAttribute('product-grid-view', mobileView);
    grid.style.setProperty(
      'grid-template-columns',
      mobileColumns === '1' ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
      'important'
    );
  });

  if (desktopLayout) {
    document.querySelectorAll('input[name="grid"]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = input.value === desktopView;
    });
  } else {
    updateMobileColumnButtons(mobileColumns);
  }
}

function applyMobileColumns(columns, persist = true) {
  const normalized = columns === '1' ? '1' : '2';

  if (persist) {
    sessionStorage.setItem(MOBILE_GRID_KEY, normalized === '1' ? 'mobile-single' : 'default');
  }

  if (!desktopMedia.matches) {
    document.querySelectorAll('ul.product-grid').forEach((grid) => {
      const view = normalized === '1' ? 'mobile-single' : 'default';
      grid.setAttribute('product-grid-view', view);
      grid.style.setProperty(
        'grid-template-columns',
        normalized === '1' ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
        'important'
      );
    });
  }

  updateMobileColumnButtons(normalized);
}

function initialiseResponsiveControls() {
  applyMode(currentMode());
  restoreProductGrid();
}

function showProductFeedback(source, message = 'Added to cart', kind = 'cart', duration = 1450) {
  if (!(source instanceof Element)) return;
  const scope =
    source.closest('product-card, product-card-link, .resource-card, .buy-buttons-block, product-info, .bce-liked-card') ||
    source.closest('main[data-template="product"]');
  if (!(scope instanceof HTMLElement)) return;

  scope.querySelector('[data-bce-action-feedback]')?.remove();
  const feedback = document.createElement('div');
  feedback.className = 'bce-action-feedback';
  feedback.dataset.bceActionFeedback = '';
  feedback.dataset.kind = kind;
  feedback.setAttribute('role', 'status');

  const icon = document.createElement('span');
  icon.className = 'bce-action-feedback__icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = kind === 'like' ? '👍' : kind === 'error' ? '!' : '✓';

  const label = document.createElement('span');
  label.textContent = message;
  feedback.append(icon, label);
  scope.appendChild(feedback);
  window.setTimeout(() => feedback.remove(), duration);
}

window.BCEProductFeedback = { show: showProductFeedback };

function visibleCartFlyDestination() {
  const candidates = Array.from(
    document.querySelectorAll(
      '[data-testid="cart-drawer-trigger"] .header-actions__cart-icon, .header-actions__cart-icon'
    )
  );

  const visibleCartIcon = candidates.find((candidate) => {
    if (!(candidate instanceof HTMLElement)) return false;
    const rect = candidate.getBoundingClientRect();
    const style = window.getComputedStyle(candidate);
    return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
  });
  if (visibleCartIcon) return visibleCartIcon;

  if (isPhysicalMobileView()) {
    const mobileTarget = document.querySelector(
      '#header-component [data-bce-header-more-toggle], #header-component .bce-account-cart-group'
    );
    if (mobileTarget instanceof HTMLElement) return mobileTarget;
  }

  return candidates.find((candidate) => candidate instanceof HTMLElement) || null;
}

function visibleCardImage(card) {
  if (!(card instanceof HTMLElement)) return null;

  const cardRect = card.getBoundingClientRect();
  const images = Array.from(card.querySelectorAll('img')).filter(
    (candidate) => candidate instanceof HTMLImageElement && candidate.currentSrc
  );

  return (
    images.find((candidate) => {
      const rect = candidate.getBoundingClientRect();
      const style = window.getComputedStyle(candidate);
      return (
        rect.width > 0 &&
        rect.height > 0 &&
        rect.right > cardRect.left + 8 &&
        rect.left < cardRect.right - 8 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        Number(style.opacity || 1) > 0.05
      );
    }) || images[0] || null
  );
}

function createPhysicalMobileFlyToCart(image, destination) {
  const sourceRect = image.getBoundingClientRect();
  const destinationRect = destination.getBoundingClientRect();
  if (
    sourceRect.width <= 0 ||
    sourceRect.height <= 0 ||
    destinationRect.width <= 0 ||
    destinationRect.height <= 0
  ) {
    return false;
  }

  const imageUrl = image.currentSrc || image.src;
  if (!imageUrl) return false;

  const size = Math.max(48, Math.min(64, sourceRect.width * 0.28));
  const startX = sourceRect.left + sourceRect.width / 2 - size / 2;
  const startY = sourceRect.top + sourceRect.height / 2 - size / 2;
  const endX = destinationRect.left + destinationRect.width / 2 - size / 2;
  const endY = destinationRect.top + destinationRect.height / 2 - size / 2;
  const travelX = endX - startX;
  const travelY = endY - startY;
  const arcY = travelY * 0.48 - 46;

  const flyer = document.createElement('span');
  flyer.setAttribute('aria-hidden', 'true');
  Object.assign(flyer.style, {
    position: 'fixed',
    left: `${startX}px`,
    top: `${startY}px`,
    width: `${size}px`,
    height: `${size}px`,
    zIndex: '2147483647',
    pointerEvents: 'none',
    borderRadius: '12px',
    backgroundColor: '#fff',
    backgroundImage: `url("${imageUrl.replaceAll('"', '%22')}")`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    boxShadow: '0 10px 28px rgb(17 18 22 / 24%)',
    willChange: 'transform, opacity',
  });
  document.body.appendChild(flyer);

  const animation = flyer.animate(
    [
      { transform: 'translate3d(0, 0, 0) scale(0.96)', opacity: 0.95 },
      {
        transform: `translate3d(${travelX * 0.46}px, ${arcY}px, 0) scale(0.78)`,
        opacity: 1,
        offset: 0.52,
      },
      {
        transform: `translate3d(${travelX}px, ${travelY}px, 0) scale(0.24)`,
        opacity: 0.28,
      },
    ],
    {
      duration: 980,
      easing: 'cubic-bezier(0.22, 0.72, 0.24, 1)',
      fill: 'forwards',
    }
  );
  animation.finished.finally(() => flyer.remove());
  return true;
}

function createFlyToCart(button) {
  const destination = visibleCartFlyDestination();
  const card = button.closest('product-card, product-card-link, .resource-card');
  const image = visibleCardImage(card);
  const imageUrl = image instanceof HTMLImageElement ? image.currentSrc || image.src : '';

  if (!destination || !imageUrl) return;

  if (isPhysicalMobileView() && image instanceof HTMLImageElement) {
    if (createPhysicalMobileFlyToCart(image, destination)) return;
  }

  if (!customElements.get('fly-to-cart')) return;
  const fly = document.createElement('fly-to-cart');
  fly.classList.add('fly-to-cart--quick');
  fly.style.setProperty('background-image', `url("${imageUrl.replaceAll('"', '%22')}")`);
  fly.style.setProperty('--start-opacity', '0');
  fly.source = button;
  fly.destination = destination;
  document.body.appendChild(fly);
}

async function addCardProduct(form) {
  if (form.dataset.bceLoading === 'true') return;

  const button = form.querySelector('button[type="submit"]');
  if (!(button instanceof HTMLButtonElement)) return;

  form.dataset.bceLoading = 'true';
  button.disabled = true;
  button.classList.add('is-loading');
  button.removeAttribute('data-bce-error');

  const formData = new FormData(form);
  formData.delete('return_to');
  const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
  optimisticCartIncrement(quantity, button);
  createFlyToCart(button);

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : '')
    .filter(Boolean);

  if (sectionIds.length > 0) {
    formData.set('sections', sectionIds.join(','));
  }

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url || form.action, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });
    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Unable to add this product');
    }

    const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    const cart = await cartResponse.json();
    const variantId = String(formData.get('id') || '');

    renderCartIndicators(cart, false);
    window.__bceOptimisticCart = null;

    const eventData = {
      source: 'bce-card-actions',
      itemCount: cart.item_count,
      productId: form.dataset.productId || '',
      sections: result.sections,
    };

    if (isPhysicalMobileView()) {
      const drawer = document.querySelector('cart-drawer-component');
      const refreshed =
        drawer instanceof HTMLElement &&
        refreshMobileCartDrawerInner(drawer, sectionIds, result.sections, cart);
      const cartItems = refreshed
        ? drawer.querySelector('cart-items-component[data-section-id]')
        : null;

      if (!(cartItems instanceof HTMLElement)) {
        throw new Error('Unable to refresh the mobile cart drawer');
      }

      // Preserve the live mobile dialog/header while notifying the rest of the theme.
      // Dispatching from its cart-items component prevents a full header-section morph.
      cartItems.dispatchEvent(new CartAddEvent(cart, variantId, eventData));
    } else {
      document.dispatchEvent(new CartAddEvent(cart, variantId, eventData));
    }

    setAddedFeedback(button, 1500);
  } catch (error) {
    window.__bceOptimisticCart = null;
    button.removeAttribute('data-bce-instant-added');
    button.removeAttribute('data-added');
    button.dataset.bceError = 'true';
    button.title = error instanceof Error ? error.message : 'Unable to add this product';
    window.setTimeout(() => button.removeAttribute('data-bce-error'), 2400);
    window.setTimeout(syncCartIndicators, 0);
  } finally {
    button.disabled = false;
    button.classList.remove('is-loading');
    form.dataset.bceLoading = 'false';
  }
}

function cartRoot() {
  return window.Shopify?.routes?.root || '/';
}

async function fetchCartState() {
  const response = await fetch(`${cartRoot()}cart.js`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) throw new Error('Unable to refresh the cart');
  const cart = await response.json();
  window.__bceLastCartState = cart;
  return cart;
}

function currentRenderedCartCount() {
  const cached = sessionStorage.getItem('cart-count');
  try {
    const value = Number(JSON.parse(cached || '{}')?.value);
    if (Number.isFinite(value)) return Math.max(0, value);
  } catch {
    // Ignore invalid cached state.
  }

  const visible = document.querySelector('cart-icon [ref="cartBubbleCount"]');
  const value = Number(visible?.textContent || 0);
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function setAddedFeedback(button, duration = 1500) {
  if (!(button instanceof HTMLElement)) return;
  button.dataset.bceInstantAdded = 'true';
  button.dataset.added = 'true';
  window.setTimeout(() => {
    button.removeAttribute('data-bce-instant-added');
    button.removeAttribute('data-added');
  }, duration);
}

function optimisticCartIncrement(quantity = 1, button = null) {
  const existing = window.__bceOptimisticCart;
  const base =
    existing && existing.expiresAt > Date.now()
      ? existing.total
      : currentRenderedCartCount();
  const total = base + Math.max(1, Number(quantity) || 1);

  window.__bceOptimisticCart = {
    total,
    expiresAt: Date.now() + OPTIMISTIC_CART_WINDOW,
  };

  renderCartIndicators({ item_count: total }, false);
  setAddedFeedback(button, 1800);
  return total;
}

function renderCartIndicators(cart, animate = false) {
  const itemCount = Number(cart?.item_count) || 0;

  document.querySelectorAll('cart-icon').forEach((icon) => {
    if (typeof icon.renderCartBubble === 'function') {
      icon.renderCartBubble(itemCount, false, animate);
      return;
    }

    const count = icon.querySelector('[ref="cartBubbleCount"]');
    const bubble = icon.querySelector('[ref="cartBubble"]');
    if (count) count.textContent = itemCount < 100 ? String(itemCount) : '';
    if (bubble) bubble.classList.toggle('visually-hidden', itemCount === 0);
  });

  document.querySelectorAll('.bce-cart-drawer-count').forEach((label) => {
    label.textContent = itemCount === 1 ? '1 item' : `${itemCount} items`;
  });

  sessionStorage.setItem(
    'cart-count',
    JSON.stringify({ value: String(itemCount), timestamp: Date.now() })
  );
}

async function syncCartIndicators() {
  try {
    const cart = await fetchCartState();
    renderCartIndicators(cart, false);
    return cart;
  } catch {
    return null;
  }
}

function variantTotals(items) {
  const totals = new Map();
  items.forEach((item) => {
    const id = String(item.variant_id || item.id || '');
    if (!id) return;
    totals.set(id, (totals.get(id) || 0) + (Number(item.quantity) || 0));
  });
  return totals;
}

function rememberIsolatedBuyNow(cart, variantId) {
  const normalizedId = String(variantId || '');
  if (!normalizedId || !cart) return false;

  const originalItems = Array.isArray(cart.items) ? cart.items : [];
  const expected = variantTotals(originalItems);
  expected.set(normalizedId, (expected.get(normalizedId) || 0) + 1);
  sessionStorage.setItem(
    BUY_NOW_PENDING_KEY,
    JSON.stringify({
      expiresAt: Date.now() + 2 * 60 * 60 * 1000,
      hadExistingItems: originalItems.some((item) => (Number(item.quantity) || 0) > 0),
      items: Array.from(expected, ([id, quantity]) => ({ id, quantity })),
    })
  );
  return true;
}

function productPageVariantId(source) {
  const scope = source?.closest(
    'product-info, product-form, form[action*="/cart/add"], [data-product-id]'
  );
  const input =
    scope?.querySelector('[name="id"]') ||
    document.querySelector('main form[action*="/cart/add"] [name="id"]');
  return input instanceof HTMLInputElement || input instanceof HTMLSelectElement
    ? String(input.value || '')
    : '';
}

async function beginIsolatedBuyNow(link) {
  if (link.dataset.bceLoading === 'true') return;

  const variantId = String(link.dataset.variantId || productPageVariantId(link));
  if (!variantId) {
    window.location.assign(link.href);
    return;
  }

  link.dataset.bceLoading = 'true';
  link.setAttribute('aria-busy', 'true');

  try {
    const cart = await fetchCartState();
    rememberIsolatedBuyNow(cart, variantId);
  } catch {
    sessionStorage.removeItem(BUY_NOW_PENDING_KEY);
  }

  window.location.assign(link.href);
}

function isCartPage() {
  return /^\/cart\/?$/.test(window.location.pathname);
}

function setCartRestoreState(active) {
  if (!isCartPage()) return;

  const cartMain = document.querySelector('main[data-template="cart"]');
  if (active) {
    root.dataset.bceCartRestoring = 'true';
    cartMain?.setAttribute('aria-busy', 'true');
    return;
  }

  delete root.dataset.bceCartRestoring;
  cartMain?.removeAttribute('aria-busy');
}

function cartSectionIds() {
  return Array.from(
    new Set(
      Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId || '' : ''))
        .filter(Boolean)
    )
  );
}

async function renderRestoredCartSections(sectionIds, sections = null) {
  await Promise.all(
    sectionIds.map(async (sectionId) => {
      const sectionHtml = sections?.[sectionId];

      if (typeof sectionHtml === 'string' && sectionHtml.trim()) {
        try {
          await morphSection(sectionId, sectionHtml);
          return;
        } catch {
          // Fetch a fresh section below if bundled rendering could not be applied.
        }
      }

      await sectionRenderer.renderSection(sectionId, { cache: false });
    })
  );
}

async function restoreIsolatedBuyNowCartOnce() {
  const rawPending = sessionStorage.getItem(BUY_NOW_PENDING_KEY);
  if (!rawPending) {
    setCartRestoreState(false);
    return syncCartIndicators();
  }

  let pending;
  try {
    pending = JSON.parse(rawPending);
  } catch {
    sessionStorage.removeItem(BUY_NOW_PENDING_KEY);
    setCartRestoreState(false);
    return syncCartIndicators();
  }

  if (!pending?.expiresAt || pending.expiresAt < Date.now() || !Array.isArray(pending.items)) {
    sessionStorage.removeItem(BUY_NOW_PENDING_KEY);
    setCartRestoreState(false);
    return syncCartIndicators();
  }

  const shouldMaskCart = isCartPage() && pending.hadExistingItems !== false;
  if (shouldMaskCart) setCartRestoreState(true);

  try {
    const cart = await fetchCartState();
    const current = variantTotals(cart.items || []);
    const missingItems = pending.items
      .map((item) => ({
        id: String(item.id || ''),
        quantity: Math.max(0, (Number(item.quantity) || 0) - (current.get(String(item.id || '')) || 0)),
      }))
      .filter((item) => item.id && item.quantity > 0);

    const sectionIds = cartSectionIds();
    let restoredSections = null;

    if (missingItems.length > 0) {
      const addPayload = { items: missingItems };

      if (sectionIds.length > 0) {
        addPayload.sections = sectionIds.join(',');
        addPayload.sections_url = window.location.pathname;
      }

      const response = await fetch(`${cartRoot()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(addPayload),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to restore the saved cart');
      }

      restoredSections = result.sections || null;
    }

    const restoredCart = await fetchCartState();
    renderCartIndicators(restoredCart, false);

    const needsSectionRender =
      sectionIds.length > 0 &&
      (missingItems.length > 0 || (isCartPage() && pending.hadExistingItems !== false));

    if (needsSectionRender) {
      await renderRestoredCartSections(sectionIds, restoredSections);
    }

    sessionStorage.removeItem(BUY_NOW_PENDING_KEY);
    return restoredCart;
  } catch {
    return null;
  } finally {
    setCartRestoreState(false);
  }
}

function bindProductCardInteractions(scope = document) {
  const actionRows = [];
  if (scope instanceof Element && scope.matches('[data-bce-card-actions]')) actionRows.push(scope);
  scope.querySelectorAll?.('[data-bce-card-actions]').forEach((row) => actionRows.push(row));

  actionRows.forEach((row) => {
    if (!(row instanceof HTMLElement) || row.dataset.bceInteractionBound === 'true') return;
    row.dataset.bceInteractionBound = 'true';
    row.addEventListener('click', (event) => event.stopPropagation());
    row.addEventListener('pointerdown', (event) => event.stopPropagation());
  });

  const cards = [];
  if (scope instanceof Element && scope.matches('product-card, .resource-card[data-resource-type="product"]')) {
    cards.push(scope);
  }
  scope.querySelectorAll?.('product-card, .resource-card[data-resource-type="product"]').forEach((card) => cards.push(card));

  cards.forEach((card) => {
    if (!(card instanceof HTMLElement) || card.dataset.bceNavigationBound === 'true') return;
    // The theme's product transition handler treats the whole card as a link.
    // Disable it here so Like/Cart remain independent controls; the explicit
    // product link and the scoped navigation handler below still open products.
    card.removeAttribute('on:click');
    card.closest('product-card-link')?.removeAttribute('on:click');
    card.dataset.bceNavigationBound = 'true';
  });
}

function handleProductCardNavigation(event) {
  if (!(event instanceof MouseEvent) || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  if (
    Date.now() < predictiveSearchSwipeUntil &&
    target.closest('.search-modal__content [data-bce-resource-slider]')
  ) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }
  if (target.closest('[data-bce-card-actions], button, form, input, select, textarea, summary, [role="button"]')) {
    return;
  }

  const card = target.closest(
    'product-card, product-card-link, .resource-card[data-resource-type="product"], .predictive-search-results__card--product'
  );
  if (!(card instanceof HTMLElement)) return;

  const directLink = target.closest('a[href]');
  const fallbackLink = card.querySelector(
    '.product-card__link[href], .resource-card__link[href], a[href*="/products/"]'
  );
  const link = directLink instanceof HTMLAnchorElement ? directLink : fallbackLink;
  if (!(link instanceof HTMLAnchorElement) || !link.href) return;

  try {
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign(url.href);
  } catch {
    // Leave the theme's native navigation untouched when the link is invalid.
  }
}

document.addEventListener('click', handleProductCardNavigation, true);

let predictiveSearchPointerStart = null;
let predictiveSearchSwipeUntil = 0;

document.addEventListener(
  'pointerdown',
  (event) => {
    if (!isPhysicalMobileView() || event.isPrimary === false) return;
    const target = event.target instanceof Element ? event.target : null;
    const slider = target?.closest('.search-modal__content [data-bce-resource-slider]');
    if (!(slider instanceof HTMLElement)) return;
    predictiveSearchPointerStart = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, slider };
  },
  true
);

document.addEventListener(
  'pointerup',
  (event) => {
    if (event.isPrimary === false || Number(event.button) > 0) return;
    const target = event.target instanceof Element ? event.target : null;
    const start = predictiveSearchPointerStart;
    predictiveSearchPointerStart = null;
    if (!target || !start || start.pointerId !== event.pointerId || !start.slider.contains(target)) return;

    const movedX = Math.abs(event.clientX - start.x);
    const movedY = Math.abs(event.clientY - start.y);
    if (movedX <= 12 || movedX <= movedY) return;

    predictiveSearchSwipeUntil = Date.now() + 600;
    const width = Math.max(1, start.slider.clientWidth);
    const count = Math.max(1, Math.round(start.slider.scrollWidth / width));
    const current = Math.round(start.slider.scrollLeft / width);
    const direction = event.clientX < start.x ? 1 : -1;
    const next = Math.min(count - 1, Math.max(0, current + direction));
    start.slider.scrollTo({ left: next * width, behavior: 'smooth' });
    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);

document.addEventListener('pointercancel', () => {
  predictiveSearchPointerStart = null;
}, true);

async function clearCart(button) {
  if (button.dataset.bceLoading === 'true') return;
  button.dataset.bceLoading = 'true';
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  try {
    const response = await fetchWithTimeout(`${cartRoot()}cart/clear.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({}),
    }, 9000);
    const cart = await response.json();
    if (!response.ok || cart.status) {
      throw new Error(cart.description || cart.message || 'Unable to clear the cart');
    }

    sessionStorage.removeItem(BUY_NOW_PENDING_KEY);
    window.__bceOptimisticCart = null;
    window.__bceLastCartState = cart;
    renderCartIndicators(cart, false);

    const drawer = button.closest('cart-drawer-component');
    const template = drawer?.querySelector('#empty-cart-template');
    const inner = drawer?.querySelector('.cart-drawer__inner');
    const cartItems = inner?.querySelector('cart-items-component[data-section-id]');
    if (
      template instanceof HTMLTemplateElement &&
      inner instanceof HTMLElement &&
      cartItems instanceof HTMLElement
    ) {
      // Keep the live cart component and its section ID mounted. Only its
      // contents become empty, so the next add can request and render sections.
      cartItems.replaceChildren(template.content.cloneNode(true));
      drawer?.querySelector('[data-testid="cart-drawer-dialog"]')?.classList.add('cart-drawer--empty');
      return;
    }

    const sectionIds = cartSectionIds();
    if (sectionIds.length > 0) {
      try {
        await promiseWithTimeout(renderRestoredCartSections(sectionIds), 6500);
      } catch {
        if (isCartPage()) window.location.reload();
      }
      return;
    }

    if (isCartPage()) window.location.reload();
  } catch (error) {
    button.dataset.bceError = 'true';
    button.title = error instanceof Error ? error.message : 'Unable to clear the cart';
    const previousLabel = button.textContent;
    button.textContent = 'Try again';
    window.setTimeout(() => {
      button.removeAttribute('data-bce-error');
      button.textContent = previousLabel;
    }, 2600);
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.dataset.bceLoading = 'false';
  }
}


function restoreIsolatedBuyNowCart() {
  if (!buyNowRestorePromise) {
    buyNowRestorePromise = restoreIsolatedBuyNowCartOnce().finally(() => {
      buyNowRestorePromise = null;
    });
  }
  return buyNowRestorePromise;
}

document.addEventListener(
  'pointerdown',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const acceleratedCheckout = target?.closest(
      '.shopify-payment-button, shopify-accelerated-checkout, [data-testid*="accelerated-checkout"]'
    );
    if (!acceleratedCheckout) return;

    const variantId = productPageVariantId(acceleratedCheckout);
    if (!variantId) return;

    if (window.__bceLastCartState) {
      rememberIsolatedBuyNow(window.__bceLastCartState, variantId);
      return;
    }

    fetchCartState()
      .then((cart) => rememberIsolatedBuyNow(cart, variantId))
      .catch(() => sessionStorage.removeItem(BUY_NOW_PENDING_KEY));
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const buyNowLink = target?.closest('[data-bce-buy-now]');

    if (!(buyNowLink instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    beginIsolatedBuyNow(buyNowLink);
  },
  true
);


/* Preserve Shopify's subsetted section CSS during the physical-mobile inner refresh.
 * The Section Rendering API returns these styles beside the section markup; replacing
 * only .cart-drawer__inner would otherwise discard them until a full page refresh. */
function installRenderedSectionStyles(parsed) {
  if (!(parsed instanceof Document)) return;

  const currentStyles = Array.from(
    document.querySelectorAll('style[data-section-stylesheet]')
  );

  parsed.querySelectorAll('style[data-section-stylesheet]').forEach((style) => {
    const cssText = style.textContent || '';
    if (!cssText.trim()) return;

    const alreadyInstalled = currentStyles.some(
      (current) => (current.textContent || '') === cssText
    );
    if (alreadyInstalled) return;

    const installedStyle = style.cloneNode(true);
    installedStyle.setAttribute('data-bce-section-stylesheet', 'mobile-cart');
    document.head.appendChild(installedStyle);
    currentStyles.push(installedStyle);
  });
}

/* BCE physical-mobile drawer inner refresh — Aug 28 2026 */
function refreshMobileCartDrawerInner(drawer, sectionIds, sections, cart) {
  const inner = drawer?.querySelector('.cart-drawer__inner');
  if (!(inner instanceof HTMLElement)) return false;

  const itemCount = Math.max(0, Number(cart?.item_count) || 0);
  if (itemCount === 0) {
    const emptyTemplate = drawer.querySelector('#empty-cart-template');
    const cartItems = inner.querySelector('cart-items-component[data-section-id]');
    if (
      emptyTemplate instanceof HTMLTemplateElement &&
      cartItems instanceof HTMLElement
    ) {
      cartItems.replaceChildren(emptyTemplate.content.cloneNode(true));
      drawer
        .querySelector('[data-testid="cart-drawer-dialog"]')
        ?.classList.add('cart-drawer--empty');
      return true;
    }
  }

  for (const sectionId of sectionIds) {
    const sectionHtml = sections?.[sectionId];
    if (typeof sectionHtml !== 'string' || !sectionHtml.trim()) continue;

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    installRenderedSectionStyles(parsed);
    const nextInner = parsed.querySelector('.cart-drawer__inner');
    if (!(nextInner instanceof HTMLElement)) continue;

    const nextChildren = Array.from(nextInner.childNodes, (node) => node.cloneNode(true));
    inner.replaceChildren(...nextChildren);
    drawer
      .querySelector('[data-testid="cart-drawer-dialog"]')
      ?.classList.toggle('cart-drawer--empty', itemCount === 0);
    inner.querySelectorAll('cart-items-component.cart-items-disabled').forEach((component) => {
      component.classList.remove('cart-items-disabled');
    });
    if (isPhysicalMobileView()) {
      inner
        .querySelectorAll('.cart-items__remove[on\\:click^="/onLineItemRemove/"]')
        .forEach((button) => armMobileCartRemoveButton(button));
    }
    return true;
  }

  return false;
}

/* BCE physical-mobile single-request cart delete — Aug 28 2026 */
function armMobileCartRemoveButton(button) {
  if (!(button instanceof HTMLButtonElement)) return false;

  const action = button.getAttribute('on:click') || '';
  const line = Number(action.split('/').filter(Boolean).pop());
  const row = button.closest('[data-key]');
  const key = row instanceof HTMLElement ? row.dataset.key || '' : '';

  if (!key && (!Number.isInteger(line) || line < 1)) return false;

  if (Number.isInteger(line) && line > 0) {
    button.dataset.bceMobileRemoveLine = String(line);
  }
  if (key) button.dataset.bceMobileRemoveKey = key;

  // Disable Shopify's declarative remove action before the click event.
  // The physical-mobile handler below is then the only cart/change request.
  button.removeAttribute('on:click');
  return true;
}

function animateMobileCartLineRemoval(button) {
  const row = button.closest('.cart-items__table-row');
  if (!(row instanceof HTMLElement)) return Promise.resolve();

  const drawer = button.closest('cart-drawer-component');
  const rowKey = row.dataset.key || '';
  const rows = Array.from(drawer?.querySelectorAll('.cart-items__table-row') || []).filter(
    (candidate) =>
      candidate instanceof HTMLElement &&
      (candidate === row || (rowKey && candidate.dataset.parentKey === rowKey))
  );
  if (rows.length === 0) return Promise.resolve();

  const animations = rows.map((candidate) => {
    const rect = candidate.getBoundingClientRect();
    const style = window.getComputedStyle(candidate);
    candidate.style.overflow = 'hidden';

    return candidate.animate(
      [
        {
          height: `${rect.height}px`,
          marginBottom: style.marginBottom,
          paddingBottom: style.paddingBottom,
          opacity: 1,
          transform: 'translateY(0)',
        },
        {
          height: '0px',
          marginBottom: '0px',
          paddingBottom: '0px',
          opacity: 0,
          transform: 'translateY(-12px)',
          borderColor: 'transparent',
        },
      ],
      {
        duration: 460,
        easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
        fill: 'forwards',
      }
    );
  });

  return Promise.all(animations.map((animation) => animation.finished.catch(() => undefined)));
}

async function removeMobileCartLine(button) {
  if (button.dataset.bceLoading === 'true') return;

  const drawer = button.closest('cart-drawer-component');
  const line = Number(button.dataset.bceMobileRemoveLine || '');
  const key = button.dataset.bceMobileRemoveKey || '';
  if (!key && (!Number.isInteger(line) || line < 1)) return;

  button.dataset.bceLoading = 'true';
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.closest('cart-items-component')?.classList.add('cart-items-disabled');

  try {
    const sectionIds = cartSectionIds();
    const changePayload = {
      quantity: 0,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };
    if (key) changePayload.id = key;
    else changePayload.line = line;

    const response = await fetchWithTimeout(
      `${cartRoot()}cart/change.js`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(changePayload),
      },
      9000
    );
    const cart = await response.json();

    if (!response.ok || cart.errors || cart.status >= 400) {
      throw new Error(cart.description || cart.message || 'Unable to remove this item');
    }

    sessionStorage.removeItem(BUY_NOW_PENDING_KEY);
    window.__bceOptimisticCart = null;
    window.__bceLastCartState = cart;
    renderCartIndicators(cart, false);
    await animateMobileCartLineRemoval(button);

    if (
      !(drawer instanceof HTMLElement) ||
      !refreshMobileCartDrawerInner(drawer, sectionIds, cart.sections, cart)
    ) {
      throw new Error('Unable to refresh the cart drawer');
    }
  } catch (error) {
    button.dataset.bceError = 'true';
    button.title = error instanceof Error ? error.message : 'Unable to remove this item';
    window.setTimeout(() => button.removeAttribute('data-bce-error'), 2400);
    window.setTimeout(syncCartIndicators, 0);
  } finally {
    document.querySelectorAll('cart-items-component.cart-items-disabled').forEach((component) => {
      component.classList.remove('cart-items-disabled');
    });
    if (button.isConnected) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.dataset.bceLoading = 'false';
    }
  }
}


/* BCE physical-mobile single-request cart quantity — Aug 30 2026 */
function armMobileCartQuantityButton(button) {
  if (!(button instanceof HTMLButtonElement)) return false;

  const selector = button.closest('cart-quantity-selector-component');
  const input = selector?.querySelector('input[ref="quantityInput"][data-cart-line]');
  const line = Number(input?.dataset.cartLine || '');
  if (
    !(selector instanceof HTMLElement) ||
    !(input instanceof HTMLInputElement) ||
    !Number.isInteger(line) ||
    line < 1
  ) {
    return false;
  }

  const direction = button.name === 'plus' ? 1 : button.name === 'minus' ? -1 : 0;
  if (direction === 0) return false;

  const nativeAction = button.getAttribute('on:click');
  if (nativeAction) button.dataset.bceMobileQuantityAction = nativeAction;
  button.dataset.bceMobileQuantityDirection = String(direction);

  // Touch/pointerdown calls this before click, so Shopify's native quantity
  // handler cannot send a second cart/change request for the same tap.
  button.removeAttribute('on:click');
  return true;
}

function restoreMobileCartQuantityButton(button) {
  if (!(button instanceof HTMLButtonElement)) return;
  const nativeAction = button.dataset.bceMobileQuantityAction;
  if (nativeAction) button.setAttribute('on:click', nativeAction);
  delete button.dataset.bceMobileQuantityAction;
}

function syncMobileCartText(currentRoot, nextRoot, selector) {
  const currentNode = currentRoot.querySelector(selector);
  const nextNode = nextRoot.querySelector(selector);
  if (!(currentNode instanceof HTMLElement) || !(nextNode instanceof HTMLElement)) return;

  currentNode.textContent = nextNode.textContent;
  if (nextNode.hasAttribute('value')) {
    currentNode.setAttribute('value', nextNode.getAttribute('value') || '');
  }
}

function refreshMobileCartQuantityDisplay(drawer, sectionIds, sections, key, line) {
  const currentInner = drawer?.querySelector('.cart-drawer__inner');
  if (!(currentInner instanceof HTMLElement)) return false;

  for (const sectionId of sectionIds) {
    const sectionHtml = sections?.[sectionId];
    if (typeof sectionHtml !== 'string' || !sectionHtml.trim()) continue;

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const nextInner = parsed.querySelector('.cart-drawer__inner');
    if (!(nextInner instanceof HTMLElement)) continue;

    const currentRows = Array.from(currentInner.querySelectorAll('.cart-items__table-row[data-key]'));
    const nextRows = Array.from(nextInner.querySelectorAll('.cart-items__table-row[data-key]'));
    const currentRow =
      currentRows.find((candidate) => candidate instanceof HTMLElement && candidate.dataset.key === key) ||
      currentRows[line - 1];
    const nextRow =
      nextRows.find((candidate) => candidate instanceof HTMLElement && candidate.dataset.key === key) ||
      nextRows[line - 1];

    if (!(currentRow instanceof HTMLElement) || !(nextRow instanceof HTMLElement)) continue;

    const currentPrice = currentRow.querySelector('.cart-items__price');
    const nextPrice = nextRow.querySelector('.cart-items__price');
    if (currentPrice instanceof HTMLElement && nextPrice instanceof HTMLElement) {
      currentPrice.replaceChildren(
        ...Array.from(nextPrice.childNodes, (node) => node.cloneNode(true))
      );
    }

    const currentInput = currentRow.querySelector('input[ref="quantityInput"][data-cart-line]');
    const nextInput = nextRow.querySelector('input[ref="quantityInput"][data-cart-line]');
    if (currentInput instanceof HTMLInputElement && nextInput instanceof HTMLInputElement) {
      currentInput.value = nextInput.value;
      currentInput.dataset.cartQuantity = nextInput.dataset.cartQuantity || nextInput.value;
    }

    const currentOriginalTotals = currentInner.querySelector('.cart-totals__original-container');
    const nextOriginalTotals = nextInner.querySelector('.cart-totals__original-container');
    if (
      currentOriginalTotals instanceof HTMLElement &&
      nextOriginalTotals instanceof HTMLElement
    ) {
      currentOriginalTotals.replaceChildren(
        ...Array.from(nextOriginalTotals.childNodes, (node) => node.cloneNode(true))
      );
    }

    [
      '[ref="cartItemCount"]',
      '.bce-cart-list-tools > span',
      '.cart-items__table caption span',
      '[data-testid="cart-subtotal-value"]',
      '[data-testid="cart-total-value"]',
    ].forEach((selector) => syncMobileCartText(currentInner, nextInner, selector));

    const quantitySelector = currentRow.querySelector('cart-quantity-selector-component');
    if (
      quantitySelector instanceof HTMLElement &&
      typeof quantitySelector.updateButtonStates === 'function'
    ) {
      quantitySelector.updateButtonStates();
    }

    return true;
  }

  return false;
}

async function updateMobileCartQuantity(button) {
  if (button.dataset.bceLoading === 'true') return;

  const drawer = button.closest('cart-drawer-component');
  const selector = button.closest('cart-quantity-selector-component');
  const input = selector?.querySelector('input[ref="quantityInput"][data-cart-line]');
  const line = Number(input?.dataset.cartLine || '');
  const direction = Number(button.dataset.bceMobileQuantityDirection || '');
  if (
    !(drawer instanceof HTMLElement) ||
    !(selector instanceof HTMLElement) ||
    !(input instanceof HTMLInputElement) ||
    !Number.isInteger(line) ||
    line < 1 ||
    ![-1, 1].includes(direction)
  ) {
    return;
  }

  const currentQuantity = Number(input.value);
  const step = Math.max(1, Number(input.step) || 1);
  const minimum = Number.isFinite(Number(input.min)) ? Number(input.min) : 1;
  const maximum =
    input.max !== '' && Number.isFinite(Number(input.max)) ? Number(input.max) : Number.POSITIVE_INFINITY;
  const nextQuantity = Math.min(maximum, Math.max(minimum, currentQuantity + direction * step));
  if (!Number.isFinite(currentQuantity) || nextQuantity === currentQuantity) {
    restoreMobileCartQuantityButton(button);
    return;
  }

  const row = button.closest('[data-key]');
  const key = row instanceof HTMLElement ? row.dataset.key || '' : '';
  const selectorButtons = Array.from(selector.querySelectorAll('button')).filter(
    (candidate) => candidate instanceof HTMLButtonElement
  );
  const cartTotal = drawer.querySelector('text-component[ref="cartTotal"]');

  button.dataset.bceLoading = 'true';
  input.value = String(nextQuantity);
  input.setAttribute('aria-busy', 'true');
  if (cartTotal instanceof HTMLElement && typeof cartTotal.shimmer === 'function') {
    cartTotal.shimmer();
  }
  selectorButtons.forEach((candidate) => {
    candidate.disabled = true;
  });
  selector.closest('cart-items-component')?.classList.add('cart-items-disabled');

  try {
    const sectionIds = cartSectionIds();
    const changePayload = {
      quantity: nextQuantity,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };
    if (key) changePayload.id = key;
    else changePayload.line = line;

    const response = await fetchWithTimeout(
      `${cartRoot()}cart/change.js`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(changePayload),
      },
      9000
    );
    const cart = await response.json();

    if (!response.ok || cart.errors || Number(cart.status) >= 400) {
      throw new Error(cart.description || cart.message || 'Unable to update this quantity');
    }

    sessionStorage.removeItem(BUY_NOW_PENDING_KEY);
    window.__bceOptimisticCart = null;
    window.__bceLastCartState = cart;
    renderCartIndicators(cart, false);

    // Hydrate the existing cart section instead of replacing every child of
    // the open drawer. This follows the theme's native quantity-update path
    // and preserves the drawer's scroll container and active DOM state.
    let sectionRefreshed = false;
    for (const sectionId of sectionIds) {
      const sectionHtml = cart.sections?.[sectionId];
      if (typeof sectionHtml !== 'string' || !sectionHtml.trim()) continue;
      const parsedSection = new DOMParser().parseFromString(sectionHtml, 'text/html');
      if (!(parsedSection.querySelector('cart-drawer-component') instanceof HTMLElement)) continue;
      await morphSection(sectionId, sectionHtml, { mode: 'hydration' });
      sectionRefreshed = true;
      break;
    }
    if (!sectionRefreshed) {
      throw new Error('Unable to update the cart drawer');
    }

    const refreshedDrawer = document.querySelector('cart-drawer-component');
    if (!(refreshedDrawer instanceof HTMLElement)) {
      throw new Error('Unable to restore the cart drawer');
    }

    // Refresh the current total and savings nodes after section hydration.
    const updatedTotal = refreshedDrawer.querySelector('text-component[ref="cartTotal"]');
    if (updatedTotal instanceof HTMLElement && typeof updatedTotal.shimmer === 'function') {
      updatedTotal.shimmer();
      updatedTotal.addEventListener(
        'animationiteration',
        () => updatedTotal.removeAttribute('shimmer'),
        { once: true }
      );
    }

    const updatedSavings = refreshedDrawer.querySelector('.cart-totals__discount-savings-value');
    if (updatedSavings instanceof HTMLElement) {
      updatedSavings.setAttribute('shimmer', '');
      updatedSavings.addEventListener('animationiteration', () => {
        updatedSavings.removeAttribute('shimmer');
      }, { once: true });
    }
  } catch (error) {
    input.value = String(currentQuantity);
    button.dataset.bceError = 'true';
    button.title = error instanceof Error ? error.message : 'Unable to update this quantity';
    window.setTimeout(() => button.removeAttribute('data-bce-error'), 2400);
    window.setTimeout(syncCartIndicators, 0);
  } finally {
    document.querySelectorAll('cart-items-component.cart-items-disabled').forEach((component) => {
      component.classList.remove('cart-items-disabled');
    });
    if (input.isConnected) input.removeAttribute('aria-busy');
    if (cartTotal instanceof HTMLElement && cartTotal.isConnected) {
      cartTotal.removeAttribute('shimmer');
    }
    if (selector.isConnected) {
      selectorButtons.forEach((candidate) => restoreMobileCartQuantityButton(candidate));
      if (typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      } else {
        selectorButtons.forEach((candidate) => {
          candidate.disabled = false;
        });
      }
    }
    if (button.isConnected) button.dataset.bceLoading = 'false';
  }
}


/* Physical-mobile Android Back: use the theme's native dialog close workflow once. */
document.addEventListener(
  'cancel',
  (event) => {
    if (!isPhysicalMobileView()) return;
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.matches('#search-modal > dialog')) return;
    const component = dialog.closest('dialog-component');
    if (!(component instanceof HTMLElement) || typeof component.closeDialog !== 'function') return;
    event.preventDefault();
    component.closeDialog();
  },
  true
);

document.addEventListener(
  'pointerdown',
  (event) => {
    if (!isPhysicalMobileView() || event.isPrimary === false) return;
    const target = event.target instanceof Element ? event.target : null;
    const quantityButton = target?.closest(
      'cart-drawer-component cart-quantity-selector-component .quantity-minus, cart-drawer-component cart-quantity-selector-component .quantity-plus'
    );
    if (quantityButton instanceof HTMLButtonElement) {
      armMobileCartQuantityButton(quantityButton);
    }
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    if (!isPhysicalMobileView()) return;
    const target = event.target instanceof Element ? event.target : null;
    const quantityButton = target?.closest(
      'cart-drawer-component cart-quantity-selector-component .quantity-minus, cart-drawer-component cart-quantity-selector-component .quantity-plus'
    );
    if (!(quantityButton instanceof HTMLButtonElement)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!armMobileCartQuantityButton(quantityButton)) return;
    updateMobileCartQuantity(quantityButton);
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    if (!isPhysicalMobileView()) return;
    const target = event.target instanceof Element ? event.target : null;
    const removeButton = target?.closest(
      'cart-drawer-component .cart-items__remove[on\\:click^="/onLineItemRemove/"], cart-drawer-component .cart-items__remove[data-bce-mobile-remove-line], cart-drawer-component .cart-items__remove[data-bce-mobile-remove-key]'
    );
    if (!(removeButton instanceof HTMLButtonElement)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!armMobileCartRemoveButton(removeButton)) return;
    removeMobileCartLine(removeButton);
  },
  true
);


document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const clearButton = target?.closest('[data-bce-clear-cart]');
    if (!(clearButton instanceof HTMLButtonElement)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    clearCart(clearButton);
  },
  true
);

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const viewButton = target.closest('[data-bce-view]');
  if (viewButton instanceof HTMLButtonElement && smallTouchDevice) {
    const mode = viewButton.dataset.bceView === 'desktop' ? 'desktop' : 'mobile';
    localStorage.setItem(STORAGE_KEY, mode);
    applyMode(mode);
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    window.setTimeout(restoreProductGrid, 80);
    return;
  }

  const columnButton = target.closest('[data-bce-product-columns]');
  if (columnButton instanceof HTMLButtonElement) {
    applyMobileColumns(columnButton.dataset.bceProductColumns);
  }
});

document.addEventListener(
  'submit',
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLFormElement)) return;

    if (target.matches('.bce-card-actions__cart')) {
      event.preventDefault();
      event.stopImmediatePropagation();

      const shouldShowDesktopSkeleton =
        !isPhysicalMobileView() &&
        target.dataset.bceLoading !== 'true' &&
        currentRenderedCartCount() === 0;
      const request = addCardProduct(target);

      if (isPhysicalMobileView()) {
        trackMobileCartAdd(request);
      } else if (shouldShowDesktopSkeleton) {
        trackDesktopCartSkeleton(request, target);
      }
      return;
    }

    if (!target.matches('form[action*="/cart/add"]')) return;

    if (isPhysicalMobileView()) {
      beginMobileProductAdd();
    } else {
      beginDesktopProductSkeleton(target);
    }

    const submitter =
      event.submitter instanceof HTMLButtonElement
        ? event.submitter
        : target.querySelector('button[type="submit"]');
    const quantityInput = target.querySelector('[name="quantity"]');
    const quantity =
      quantityInput instanceof HTMLInputElement ? Math.max(1, Number(quantityInput.value) || 1) : 1;
    optimisticCartIncrement(quantity, submitter);
  },
  true
);

document.addEventListener(
  CartAddEvent.eventName,
  (event) => {
    const data = event.detail?.data;

    if (
      !isPhysicalMobileView() &&
      desktopProductSkeletonPending &&
      data?.source === 'product-form-component'
    ) {
      settleDesktopProductSkeleton(data.didError === true);
    }

    const isMobileProductFormAdd =
      isPhysicalMobileView() &&
      mobileProductAddPending &&
      data?.source === 'product-form-component' &&
      data.didError !== true;

    const optimistic = window.__bceOptimisticCart;
    if (
      optimistic &&
      optimistic.expiresAt > Date.now() &&
      data &&
      data.source !== 'bce-card-actions'
    ) {
      data.itemCount = optimistic.total;
      window.__bceOptimisticCart = null;
      window.setTimeout(syncCartIndicators, 0);
    }

    if (isMobileProductFormAdd) {
      const drawer = document.querySelector('cart-drawer-component');
      const cart = event.detail?.resource;
      const sectionIds = cartSectionIds();
      const refreshed =
        drawer instanceof HTMLElement &&
        cart &&
        Number.isFinite(Number(cart.item_count)) &&
        refreshMobileCartDrawerInner(drawer, sectionIds, data.sections, cart);
      const cartItems = refreshed
        ? drawer.querySelector('cart-items-component[data-section-id]')
        : null;

      if (cartItems instanceof HTMLElement) {
        event.stopImmediatePropagation();
        renderCartIndicators(cart, false);

        cartItems.dispatchEvent(
          new CartAddEvent(cart, event.detail?.sourceId || '', {
            ...data,
            source: 'bce-mobile-product-form',
            itemCount: Number(cart.item_count),
          })
        );

        settleMobileProductAdd();
        return;
      }

      settleMobileProductAdd();
    }

    if (!data || data.source === 'bce-card-actions') return;
    if (optimistic && optimistic.expiresAt > Date.now()) {
      data.source = 'bce-optimistic-product-form';
    }
  },
  true
);

document.addEventListener(
  'discount:update',
  (event) => {
    if (!isPhysicalMobileView()) return;

    const drawer = document.querySelector('cart-drawer-component');
    const dialog = drawer?.querySelector('[data-testid="cart-drawer-dialog"]');
    if (!(drawer instanceof HTMLElement) || !(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    const cart = event.detail?.resource;
    if (!cart || !cart.sections || typeof cart.item_count !== 'number') return;

    const sectionIds = cartSectionIds();
    const refreshed = refreshMobileCartDrawerInner(drawer, sectionIds, cart.sections, cart);
    if (!refreshed) return;

    event.stopImmediatePropagation();
    renderCartIndicators(cart, false);
  },
  true
);

document.addEventListener(
  'cart:error',
  () => {
    if (isPhysicalMobileView()) {
      settleMobileProductAdd();
    } else {
      settleDesktopProductSkeleton(true);
    }
    window.__bceOptimisticCart = null;
    document.querySelectorAll('[data-bce-instant-added]').forEach((button) => {
      button.removeAttribute('data-bce-instant-added');
      button.removeAttribute('data-added');
    });
    window.setTimeout(syncCartIndicators, 0);
  },
  true
);

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;

  if (target.name === 'grid') {
    sessionStorage.setItem(DESKTOP_GRID_KEY, target.value);
    window.setTimeout(restoreProductGrid, 0);
  }

  if (target.name === 'grid-mobile') {
    sessionStorage.setItem(MOBILE_GRID_KEY, target.value);
    window.setTimeout(restoreProductGrid, 0);
  }
});

document.addEventListener('shopify:section:load', () => {
  window.setTimeout(restoreProductGrid, 0);
  window.setTimeout(syncCartIndicators, 0);
});
window.addEventListener('pageshow', () => {
  window.setTimeout(restoreProductGrid, 0);
  window.setTimeout(restoreIsolatedBuyNowCart, 0);
});
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') window.setTimeout(syncCartIndicators, 0);
});
document.addEventListener(
  'click',
  (event) => {
    if (!isPhysicalMobileView() || !mobileCardAddPromise) return;

    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('[data-testid="cart-drawer-trigger"]');
    if (!(trigger instanceof HTMLButtonElement)) return;

    // Keep the native drawer click untouched and cover only the pending cart content.
    trackMobileCartDrawerSkeleton(mobileCardAddPromise);
  },
  true
);

document.addEventListener(
  'pointerdown',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-testid="cart-drawer-trigger"], .action__cart')) {
      if (isPhysicalMobileView() && mobileCardAddPromise) return;
      syncCartIndicators();
    }
  },
  true
);
desktopMedia.addEventListener('change', () => window.setTimeout(restoreProductGrid, 0));

let viewButtonSyncFrame = 0;

function ensureMobileFixedViewSwitchStyles() {
  if (document.getElementById('bce-mobile-fixed-view-switch-styles')) return;

  const style = document.createElement('style');
  style.id = 'bce-mobile-fixed-view-switch-styles';
  style.textContent = `
    html[data-bce-physical-mobile='true']:is([data-bce-view-mode='mobile'], [data-bce-view-mode='desktop'])
      body > .bce-floating-view-switch[data-bce-viewport-fixed='true'] {
      position: fixed !important;
      top: var(--bce-fixed-view-switch-top, 111px) !important;
      right: 6px !important;
      z-index: 44 !important;
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      width: 33px !important;
      height: 66px !important;
      margin: 0 !important;
      padding: 2px !important;
      gap: 0 !important;
      overflow: hidden !important;
      border: 1px solid rgba(227, 6, 19, 0.22) !important;
      border-radius: 999px !important;
      background: rgba(255, 255, 255, 0.96) !important;
      box-shadow: 0 8px 22px rgba(14, 18, 25, 0.12) !important;
      backdrop-filter: blur(10px);
    }

    html[data-bce-physical-mobile='true'][data-bce-view-mode='desktop']
      body > .bce-floating-view-switch[data-bce-viewport-fixed='true'] {
      top: calc(var(--bce-fixed-view-switch-top, 111px) + 20px) !important;
    }

    html[data-bce-physical-mobile='true']:is([data-bce-view-mode='mobile'], [data-bce-view-mode='desktop'])
      body > .bce-floating-view-switch[data-bce-viewport-fixed='true']
      .bce-viewport-switch__button {
      display: grid !important;
      place-items: center !important;
      flex: 0 0 29px !important;
      width: 28px !important;
      min-width: 28px !important;
      height: 28px !important;
      min-height: 28px !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      border-radius: 999px !important;
      color: #454a52 !important;
      background: transparent !important;
      box-shadow: none !important;
      cursor: pointer;
    }

    html[data-bce-physical-mobile='true']:is([data-bce-view-mode='mobile'], [data-bce-view-mode='desktop'])
      body > .bce-floating-view-switch[data-bce-viewport-fixed='true']
      .bce-viewport-switch__button[aria-pressed='true'] {
      color: #fff !important;
      background: #ed0014 !important;
    }

    html[data-bce-physical-mobile='true']:is([data-bce-view-mode='mobile'], [data-bce-view-mode='desktop'])
      body > .bce-floating-view-switch[data-bce-viewport-fixed='true'] svg {
      display: block !important;
      width: 14px !important;
      height: 14px !important;
      fill: none !important;
      stroke: currentColor !important;
      stroke-width: 1.8 !important;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    body:has(#Details-menu-drawer-container[open])
      > .bce-floating-view-switch[data-bce-viewport-fixed='true'],
    body:has(#header-component .bce-header-more-menu.is-open)
      > .bce-floating-view-switch[data-bce-viewport-fixed='true'],
    body:has(#header-component .bce-header-more-toggle[aria-expanded='true'])
      > .bce-floating-view-switch[data-bce-viewport-fixed='true'] {
      opacity: 0 !important;
      pointer-events: none !important;
    }
  `;
  document.head.appendChild(style);
}

function syncViewportFixedViewSwitch(mode = currentMode()) {
  if (!smallTouchDevice || !document.body) return;

  const bodyControl = document.querySelector(
    'body > .bce-floating-view-switch[data-bce-viewport-fixed="true"]'
  );
  const headerControls = Array.from(
    document.querySelectorAll('#header-component .bce-floating-view-switch')
  ).filter((control) => control instanceof HTMLElement);

  if (mode === 'mobile' || mode === 'desktop') {
    const control = bodyControl instanceof HTMLElement ? bodyControl : headerControls.shift();
    if (!(control instanceof HTMLElement)) return;

    headerControls.forEach((duplicate) => duplicate.remove());
    ensureMobileFixedViewSwitchStyles();

    if (control.parentElement !== document.body) {
      const computedTop = window.getComputedStyle(control).top;
      if (computedTop && computedTop !== 'auto') {
        control.style.setProperty('--bce-fixed-view-switch-top', computedTop);
      }
      control.dataset.bceViewportFixed = 'true';
      document.body.appendChild(control);
    }

    return;
  }

  if (!(bodyControl instanceof HTMLElement)) return;

  const host = document.querySelector('#header-component header-actions');
  if (!(host instanceof HTMLElement)) return;

  host.querySelectorAll('.bce-floating-view-switch').forEach((duplicate) => {
    if (duplicate !== bodyControl) duplicate.remove();
  });

  const liveRegion = host.querySelector('[ref="liveRegion"]');
  host.insertBefore(bodyControl, liveRegion instanceof Node ? liveRegion : null);
  bodyControl.removeAttribute('data-bce-viewport-fixed');
  bodyControl.style.removeProperty('--bce-fixed-view-switch-top');
}

function scheduleViewButtonStateSync() {
  if (!smallTouchDevice || viewButtonSyncFrame) return;

  viewButtonSyncFrame = window.requestAnimationFrame(() => {
    viewButtonSyncFrame = 0;
    const mode = currentMode();
    syncViewportFixedViewSwitch(mode);
    updateViewButtons(mode);
  });
}

const viewButtonStateObserver = new MutationObserver((mutations) => {
  const viewStateChanged = mutations.some((mutation) => {
    if (
      mutation.type === 'attributes' &&
      mutation.target instanceof Element &&
      mutation.target.matches('[data-bce-view]')
    ) {
      return true;
    }

    return Array.from(mutation.addedNodes).some(
      (node) =>
        node instanceof Element &&
        (node.matches('[data-bce-view]') || node.querySelector('[data-bce-view]'))
    );
  });

  if (viewStateChanged) scheduleViewButtonStateSync();
});

const productGridObserver = new MutationObserver((mutations) => {
  const productGridAdded = mutations.some((mutation) =>
    Array.from(mutation.addedNodes).some(
      (node) =>
        node instanceof Element &&
        (node.matches('ul.product-grid') || node.querySelector('ul.product-grid'))
    )
  );

  if (productGridAdded) {
    window.requestAnimationFrame(restoreProductGrid);
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initialiseResponsiveControls();
    restoreIsolatedBuyNowCart();
    viewButtonStateObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-pressed'],
    });
    productGridObserver.observe(document.body, { childList: true, subtree: true });
  }, { once: true });
} else {
  initialiseResponsiveControls();
  restoreIsolatedBuyNowCart();
  if (document.body) {
    viewButtonStateObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-pressed'],
    });
    productGridObserver.observe(document.body, { childList: true, subtree: true });
  }
}

